/**
 * Add User wizard.
 *
 * CR-5313: rebuilt as a four-step flow so admins can:
 *   1. Capture the user profile up front (title, name, email, phone, job title, dept).
 *   2. Pick the primary company + any additional sites in the same group.
 *   3. Set per-site access (modules, role flags, notifications), with an
 *      "apply same settings to every site" toggle that drives bulk apply.
 *   4. Review the summary and send the invite.
 *
 * The legacy flow only supported one company and exposed the Compliance
 * module nowhere. This wizard layers on top of the existing
 * `/core/invitations/` endpoint plus the per_site_permissions JSON map
 * the backend now honours, so we don't need a UserProfile migration to
 * support multi-site assignments within a customer/supplier group.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  invitationAPI,
  coreAPI,
  type UserInvitationCreate,
  type UserSitePermissionFlags,
  type Role,
  type Company,
  type CompanyGroup,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  UserPlus, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2,
  Building2, Truck, FileCheck, CheckCircle, ShieldCheck, Eye,
  Mail, Phone, Briefcase, User as UserIcon,
  ClipboardList, Layers,
} from 'lucide-react';

type WizardStep = 1 | 2 | 3 | 4;

const STEPS: { num: WizardStep; label: string; icon: typeof UserIcon }[] = [
  { num: 1, label: 'Profile', icon: UserIcon },
  { num: 2, label: 'Companies', icon: Building2 },
  { num: 3, label: 'Access', icon: ShieldCheck },
  { num: 4, label: 'Review', icon: ClipboardList },
];

const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof'];

const DEFAULT_PERMISSION_FLAGS: Required<UserSitePermissionFlags> = {
  can_purchasing: true,
  can_quality: true,
  can_compliance: false,
  is_admin: false,
  can_approve: false,
  is_read_only: false,
  notify_procurement: true,
  notify_quality: true,
  notify_compliance: false,
};

const DEPARTMENTS = [
  { value: 'admin', label: 'Admin' },
  { value: 'purchasing', label: 'Procurement' },
  { value: 'quality', label: 'Quality' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'engineering', label: 'Engineering' },
];

interface ProfileFields {
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  department: string;
  message: string;
  role: number | null;
}

const EMPTY_PROFILE: ProfileFields = {
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_title: '',
  department: '',
  message: '',
  role: null,
};

export default function InviteUser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteType = (searchParams.get('type') || 'customer') as 'customer' | 'supplier';
  const isSupplierInvite = inviteType === 'supplier';

  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Step 1
  const [profile, setProfile] = useState<ProfileFields>(EMPTY_PROFILE);

  // Step 2
  const [companies, setCompanies] = useState<Company[]>([]);
  // CR-5313 round 2: Sarah wants the Primary Company dropdown to expose
  // Group + Division levels as well as Sites. We keep the underlying
  // anchor on a single site (UserProfile.company / UserSitePermission
  // rows still target Companies one-by-one), but when an admin picks a
  // Group or Division entry we expand it client-side into "primary =
  // first site under it, additional = every other site under it" so the
  // user effectively gets access across the whole branch.
  const [companyGroups, setCompanyGroups] = useState<CompanyGroup[]>([]);
  const [primaryCompanyId, setPrimaryCompanyId] = useState<number>(0);
  const [additionalSiteIds, setAdditionalSiteIds] = useState<Set<number>>(new Set());
  // Encoded selection — 'group:<id>' / 'site:<id>' — so the dropdown
  // value tracks intent (group vs site) even though storage is
  // site-centric.
  const [primarySelection, setPrimarySelection] = useState<string>('');

  // Step 3
  const [roles, setRoles] = useState<Role[]>([]);
  const [defaultFlags, setDefaultFlags] = useState<Required<UserSitePermissionFlags>>(DEFAULT_PERMISSION_FLAGS);
  // CR-5313: when true the matrix shows one shared row of toggles; when
  // false each selected site gets its own override card. Maps to the
  // backend's per_site_permissions JSON.
  const [applyToAll, setApplyToAll] = useState(true);
  const [perSiteOverrides, setPerSiteOverrides] = useState<Record<number, Required<UserSitePermissionFlags>>>({});

  const TypeIcon = isSupplierInvite ? Truck : Building2;

  useEffect(() => {
    void loadCompanies();
  }, [inviteType]);

  useEffect(() => {
    if (primaryCompanyId) void loadRoles();
  }, [primaryCompanyId, inviteType]);

  // CR-5313: when the user toggles "apply same settings" off, seed each
  // selected site's overrides from the current defaults so they have a
  // sensible starting point instead of an empty matrix.
  useEffect(() => {
    if (applyToAll) return;
    const allIds = [primaryCompanyId, ...Array.from(additionalSiteIds)].filter(Boolean);
    setPerSiteOverrides(prev => {
      const next: Record<number, Required<UserSitePermissionFlags>> = {};
      for (const id of allIds) {
        next[id] = prev[id] ?? { ...defaultFlags };
      }
      return next;
    });
  }, [applyToAll, primaryCompanyId, additionalSiteIds]);

  const loadCompanies = async () => {
    try {
      setLoadingOptions(true);
      // CR-7071: one scoped source for both invite types — the companies the admin
      // may actually grant into, plus the groups/divisions over them. Replaces the
      // prior getAccessibleCompanies / getInvitableSuppliers + getCompanyGroups combo:
      // the group-list call was Helix-only, so a customer admin got a 403, the rows
      // were dropped, and the picker fell back to a site-only list wider than what the
      // server would accept. This endpoint scopes to the same authority the
      // invitation-create floor enforces, so the picker can't offer a rejectable pick.
      const res = await coreAPI.getInviteOptions(isSupplierInvite ? 'supplier' : 'customer');
      const list = res.data.companies || [];
      setCompanies(list);
      setCompanyGroups(res.data.groups || []);

      // Sensible default for primary: user's own company if it's in the
      // list, otherwise the first entry.
      const ownInList = user?.company && list.find(c => c.id === user.company);
      if (ownInList) {
        setPrimaryCompanyId(ownInList.id);
        setPrimarySelection(`site:${ownInList.id}`);
      } else if (list.length === 1) {
        setPrimaryCompanyId(list[0].id);
        setPrimarySelection(`site:${list[0].id}`);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies.');
    } finally {
      setLoadingOptions(false);
    }
  };

  // CR-5313 round 2: return every site whose company-group chain
  // descends from ``groupId`` (the group itself OR any sub-division
  // beneath it). The chain walks via CompanyGroup.parent_group, so
  // selecting the top-level Group reaches Division → Site, and
  // selecting a Division reaches just its sites.
  const sitesUnderGroup = useMemo(() => (groupId: number): Company[] => {
    if (!groupId) return [];
    const descendants = new Set<number>([groupId]);
    let added = true;
    while (added) {
      added = false;
      for (const g of companyGroups) {
        if (g.parent_group != null && descendants.has(g.parent_group) && !descendants.has(g.id)) {
          descendants.add(g.id);
          added = true;
        }
      }
    }
    return companies.filter(c => c.group != null && descendants.has(c.group));
  }, [companies, companyGroups]);

  // CR-5313 round 3 (issue 1): only show groups whose member companies
  // belong to the same type the wizard is inviting. Without this, the
  // Customer invite dropdown surfaced supplier groups (Ironvale,
  // Skybridge, Corex, Northstar) — which a customer admin can't
  // legitimately anchor a user to. ``companies`` is already filtered
  // by the loader (getInviteOptions, scoped per invite type), so a group is
  // "in scope" iff at least one site beneath it appears in ``companies``.
  const groupsInScope = useMemo(() => {
    const visibleCompanyIds = new Set(companies.map(c => c.id));
    return companyGroups.filter(g => {
      const sites = sitesUnderGroup(g.id);
      return sites.some(s => visibleCompanyIds.has(s.id));
    });
  }, [companies, companyGroups, sitesUnderGroup]);

  // CR-5313 round 3 (issue 2): for a group with divisions beneath it,
  // pick the immediate children (divisions) so the "additional sites"
  // panel can render division headers with their sites grouped under
  // each. For a leaf group (no children) this returns an empty list
  // and the panel falls through to the flat site list.
  const divisionsUnderGroup = useMemo(() => (groupId: number): CompanyGroup[] => {
    return companyGroups.filter(g => g.parent_group === groupId);
  }, [companyGroups]);

  // Apply a Group / Division pick by expanding it into the site set
  // we actually anchor to. Primary becomes the first site under the
  // group, additional sites become every other site beneath it.
  const applyGroupSelection = (groupId: number) => {
    const sites = sitesUnderGroup(groupId);
    if (sites.length === 0) {
      setPrimaryCompanyId(0);
      setAdditionalSiteIds(new Set());
      return;
    }
    const [first, ...rest] = sites;
    setPrimaryCompanyId(first.id);
    setAdditionalSiteIds(new Set(rest.map(s => s.id)));
  };

  const loadRoles = async () => {
    try {
      const companyType = isSupplierInvite ? 'supplier' : 'customer';
      const res = await coreAPI.getRoles({ company_type: companyType, is_active: true });
      setRoles(res.data.results || []);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  // CR-5313: only show sister sites in the primary's group as candidates
  // for the multi-site step. Sites in unrelated groups would fail the
  // UserSitePermission.clean() check on the backend.
  // CR-5313 round 2 polish: when the admin anchored via a Group or
  // Division, broaden the sister-sites list to *every* site beneath
  // that anchor so the admin can see (and untick) the full set —
  // otherwise the hint reads "6 sites granted" while only one row
  // appears in the multi-site picker.
  const primaryCompany = useMemo(
    () => companies.find(c => c.id === primaryCompanyId),
    [companies, primaryCompanyId],
  );
  const sisterSites = useMemo(() => {
    if (!primaryCompany) return [];
    if (primarySelection.startsWith('group:')) {
      const anchorId = parseInt(primarySelection.split(':')[1]);
      const beneath = sitesUnderGroup(anchorId);
      return beneath.filter(c => c.id !== primaryCompany.id);
    }
    if (!primaryCompany.group) return companies.filter(c => c.id !== primaryCompany.id);
    return companies.filter(
      c => c.id !== primaryCompany.id && c.group === primaryCompany.group,
    );
  }, [companies, primaryCompany, primarySelection, sitesUnderGroup]);

  const selectedSiteIds = useMemo(() => {
    const ids = new Set<number>(additionalSiteIds);
    if (primaryCompanyId) ids.add(primaryCompanyId);
    return Array.from(ids);
  }, [primaryCompanyId, additionalSiteIds]);

  const flagsForSite = (siteId: number): Required<UserSitePermissionFlags> => {
    if (applyToAll) return defaultFlags;
    return perSiteOverrides[siteId] ?? defaultFlags;
  };

  const setFlag = (siteId: number | null, key: keyof UserSitePermissionFlags, value: boolean) => {
    if (siteId === null || applyToAll) {
      setDefaultFlags(prev => ({ ...prev, [key]: value }));
      return;
    }
    setPerSiteOverrides(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] ?? defaultFlags), [key]: value },
    }));
  };

  const validateStep = (target: WizardStep): string | null => {
    if (target >= 2 || step >= 1) {
      if (!profile.first_name.trim()) return 'First name is required.';
      if (!profile.last_name.trim()) return 'Last name is required.';
      if (!profile.email.trim()) return 'Email is required.';
    }
    if (target >= 3 || step >= 2) {
      if (!primaryCompanyId) return 'Pick a primary company.';
      // CR-7076: role is required — a conscious decision, never a default.
      if (!profile.role) return 'Pick a user role. Every user needs one.';
    }
    return null;
  };

  const goTo = (target: WizardStep) => {
    setError(null);
    if (target > step) {
      const reason = validateStep(target);
      if (reason) {
        setError(reason);
        return;
      }
    }
    setStep(target);
  };

  const handleSubmit = async () => {
    setError(null);
    const reason = validateStep(4);
    if (reason) { setError(reason); return; }
    setSubmitting(true);

    // CR-5313: per_site_permissions only carries the rows where the
    // admin actually overrode the defaults — keeps the JSON tidy and
    // makes the backend's "fall back to invitation defaults" branch
    // the common case.
    const overridesPayload: Record<string, UserSitePermissionFlags> = {};
    if (!applyToAll) {
      for (const sid of selectedSiteIds) {
        const flags = perSiteOverrides[sid];
        if (!flags) continue;
        const diff: UserSitePermissionFlags = {};
        let dirty = false;
        (Object.keys(DEFAULT_PERMISSION_FLAGS) as (keyof UserSitePermissionFlags)[]).forEach(k => {
          if (flags[k] !== defaultFlags[k]) {
            (diff as Record<string, boolean>)[k] = flags[k] as boolean;
            dirty = true;
          }
        });
        if (dirty) overridesPayload[String(sid)] = diff;
      }
    }

    const payload: UserInvitationCreate = {
      email: profile.email.trim(),
      first_name: profile.first_name.trim(),
      last_name: profile.last_name.trim(),
      salutation: profile.salutation || undefined,
      phone: profile.phone.trim() || undefined,
      company: primaryCompanyId,
      role: profile.role,
      job_title: profile.job_title || undefined,
      department: profile.department || undefined,
      message: profile.message || undefined,
      site_ids: Array.from(additionalSiteIds),
      ...defaultFlags,
      per_site_permissions: applyToAll ? undefined : overridesPayload,
    };

    try {
      await invitationAPI.createInvitation(payload);
      setSuccess(true);
      setTimeout(() => navigate('/admin/users'), 1800);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            email?: string[]; error?: string; detail?: string;
            // Platform error envelope: field errors live under
            // details.fields — without reading it, a validation refusal
            // (e.g. the allowed-email-domain policy) surfaced as the
            // generic "try again", hiding the actionable reason.
            details?: { fields?: Record<string, string[]> };
          };
        };
      };
      const fields = axiosErr.response?.data?.details?.fields;
      const firstFieldError = fields
        ? Object.values(fields).flat()[0]
        : undefined;
      setError(
        firstFieldError ||
        axiosErr.response?.data?.email?.[0] ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        'Failed to send invitation. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />Back to User Management
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-primary/10 rounded-lg">
          <UserPlus className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <TypeIcon className="h-7 w-7" />
            Add {isSupplierInvite ? 'Supplier' : 'Customer'} User
          </h1>
          <p className="text-muted-foreground">
            Profile first, then companies, then per-site access.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-6">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const isCurrent = step === s.num;
          const isDone = step > s.num;
          return (
            <div key={s.num} className="flex items-center flex-1">
              <button
                type="button"
                onClick={() => { if (s.num < step) goTo(s.num); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isCurrent ? 'bg-primary text-primary-foreground font-semibold'
                    : isDone ? 'text-primary hover:bg-primary/10 cursor-pointer'
                      : 'text-muted-foreground'
                }`}
                disabled={s.num > step}
              >
                <Icon className="h-4 w-4" />
                <span>{s.num}. {s.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 ${step > s.num ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      {success && (
        <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            Invitation sent successfully. Redirecting…
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>1. User profile</CardTitle>
            <CardDescription>Who is this user? Company assignment comes next.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Select value={profile.salutation || 'none'}
                  onValueChange={v => setProfile({ ...profile, salutation: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="first_name">First name *</Label>
                <Input id="first_name" required value={profile.first_name}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="last_name">Last name *</Label>
                <Input id="last_name" required value={profile.last_name}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />Email *</Label>
                <Input id="email" type="email" required value={profile.email}
                  onChange={e => setProfile({ ...profile, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Telephone</Label>
                <Input id="phone" value={profile.phone}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job_title" className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" />Job title</Label>
                <Input id="job_title" value={profile.job_title}
                  onChange={e => setProfile({ ...profile, job_title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={profile.department || 'none'}
                  onValueChange={v => setProfile({ ...profile, department: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Welcome message (optional)</Label>
              <Textarea id="message" rows={3} value={profile.message}
                onChange={e => setProfile({ ...profile, message: e.target.value })}
                placeholder="Add a personal note for the invitee…" />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>2. Related companies</CardTitle>
            <CardDescription>
              Pick the primary company, then any additional sites in the same group the user should have access to.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Primary company *</Label>
              <Select value={primarySelection}
                onValueChange={v => {
                  setPrimarySelection(v);
                  // Encoded selection: 'group:<id>' anchors at a group/
                  // division (expanded client-side into its sites);
                  // 'site:<id>' is the legacy site-level anchor.
                  if (v.startsWith('group:')) {
                    applyGroupSelection(parseInt(v.split(':')[1]));
                  } else if (v.startsWith('site:')) {
                    setPrimaryCompanyId(parseInt(v.split(':')[1]));
                    setAdditionalSiteIds(new Set());
                  }
                }}>
                <SelectTrigger><SelectValue placeholder="Select company, division, or group…" /></SelectTrigger>
                <SelectContent>
                  {/* CR-5313 round 2: top-level Groups come first, then
                      Divisions (groups with a parent), then individual
                      Sites. Each row tags its hierarchy level so the
                      admin can see what they're picking.
                      CR-5313 round 3 (issue 1): filtered to ``groupsInScope``
                      so a customer invite only surfaces customer groups
                      and a supplier invite only surfaces supplier groups —
                      mixing the two was Sarah's first 2026-05-20 rework
                      flag (Ironvale / Skybridge etc surfaced under the
                      customer dropdown). */}
                  {groupsInScope.filter(g => g.parent_group == null).length > 0 && (
                    <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Groups</div>
                  )}
                  {groupsInScope.filter(g => g.parent_group == null).map(g => (
                    <SelectItem key={`group-${g.id}`} value={`group:${g.id}`}>
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{g.name}</span>
                        <Badge variant="outline" className="text-[10px]">Group</Badge>
                      </div>
                    </SelectItem>
                  ))}
                  {groupsInScope.filter(g => g.parent_group != null).length > 0 && (
                    <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Divisions</div>
                  )}
                  {groupsInScope.filter(g => g.parent_group != null).map(g => (
                    <SelectItem key={`group-${g.id}`} value={`group:${g.id}`}>
                      <div className="flex items-center gap-2 pl-3">
                        <Layers className="h-4 w-4 text-muted-foreground" />
                        <span>{g.name}</span>
                        <Badge variant="outline" className="text-[10px]">Division</Badge>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Sites</div>
                  {companies.map(c => (
                    <SelectItem key={`site-${c.id}`} value={`site:${c.id}`}>
                      <div className="flex items-center gap-2 pl-6">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        {c.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Picking a Group or Division grants access to every site beneath it. Picking a Site anchors the user there and lets you tick additional sister sites below.
              </p>
              {primarySelection.startsWith('group:') && primaryCompanyId > 0 && (
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  User will be granted access to {1 + additionalSiteIds.size} site{additionalSiteIds.size === 0 ? '' : 's'} under the selected {primarySelection.includes(':') && companyGroups.find(g => g.id === parseInt(primarySelection.split(':')[1]))?.parent_group != null ? 'division' : 'group'}.
                </p>
              )}
            </div>

            {primaryCompanyId > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5" />Sites in this {primarySelection.startsWith('group:') && companyGroups.find(g => g.id === parseInt(primarySelection.split(':')[1]))?.parent_group != null ? 'division' : primarySelection.startsWith('group:') ? 'group' : 'group'}
                </Label>
                {/* CR-5313 round 3 (issues 2 + 3): when a top-level Group
                    is anchored, render its divisions as sub-headers with
                    their sites grouped under each — Sarah's expected
                    "all divisions and all sites within those divisions"
                    layout. When a Division is anchored, list every site
                    beneath the division including the primary (Sarah's
                    Toulouse-missing flag was the primary being excluded
                    from the picker because it had been auto-selected as
                    anchor). When a Site is anchored, fall back to the
                    sister-site picker. The primary site always shows as
                    locked-checked since unticking it would invalidate
                    the anchor.

                    Coverage matrix per Sarah's 2026-05-20 issues:
                    - Group anchor → divisions listed + sites grouped under each ✓
                    - Division anchor → both Toulouse and Daresbury visible ✓
                    - Site anchor → sister sites in same group (legacy) ✓
                */}
                {(() => {
                  const isGroupAnchor = primarySelection.startsWith('group:');
                  const anchorGroupId = isGroupAnchor ? parseInt(primarySelection.split(':')[1]) : null;
                  const anchorGroup = anchorGroupId ? companyGroups.find(g => g.id === anchorGroupId) : null;
                  const isTopLevelGroup = isGroupAnchor && anchorGroup?.parent_group == null;
                  const divisions = isTopLevelGroup ? divisionsUnderGroup(anchorGroupId!) : [];

                  // Build the list to render. For Group-with-divisions:
                  // sub-headers per division. Otherwise a flat list.
                  type Section = { headerLabel: string | null; sites: Company[] };
                  let sections: Section[] = [];
                  if (isTopLevelGroup && divisions.length > 0) {
                    // Each division section. Sites whose group is the
                    // top-level group directly (no division) get a
                    // "Direct sites" bucket so they don't vanish.
                    sections = divisions.map(div => ({
                      headerLabel: div.name,
                      sites: companies.filter(c => c.group === div.id),
                    }));
                    const directSites = companies.filter(c => c.group === anchorGroupId);
                    if (directSites.length > 0) {
                      sections.unshift({ headerLabel: 'Direct sites', sites: directSites });
                    }
                  } else if (isGroupAnchor && anchorGroupId) {
                    // Division anchor (no children) — flat list of every
                    // site beneath, including primary.
                    sections = [{ headerLabel: null, sites: sitesUnderGroup(anchorGroupId) }];
                  } else {
                    // Site anchor — legacy sister-sites behaviour
                    // (primary already excluded by sisterSites memo).
                    sections = [{ headerLabel: null, sites: sisterSites }];
                  }

                  const totalSites = sections.reduce((n, s) => n + s.sites.length, 0);
                  if (totalSites === 0) {
                    return (
                      <p className="text-sm text-muted-foreground italic">
                        No sites available {isGroupAnchor ? 'under this selection' : 'in this group'}.
                      </p>
                    );
                  }
                  return (
                    <div className="border rounded-lg divide-y">
                      {sections.map((section, idx) => (
                        <div key={`section-${idx}`}>
                          {section.headerLabel && (
                            <div className="px-3 py-1.5 bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-2">
                              <Layers className="h-3 w-3" />{section.headerLabel}
                            </div>
                          )}
                          {section.sites.length === 0 ? (
                            <p className="px-3 py-2 text-xs italic text-muted-foreground">No sites under this division.</p>
                          ) : section.sites.map(site => {
                            const isPrimary = site.id === primaryCompanyId;
                            const checked = isPrimary || additionalSiteIds.has(site.id);
                            return (
                              <label key={site.id}
                                className={`flex items-center gap-3 px-3 py-2 ${isPrimary ? 'bg-blue-50/60 dark:bg-blue-950/20' : 'hover:bg-muted/40 cursor-pointer'}`}>
                                <Checkbox
                                  checked={checked}
                                  disabled={isPrimary}
                                  onCheckedChange={(v: boolean | 'indeterminate') => {
                                    if (isPrimary) return;
                                    const next = new Set(additionalSiteIds);
                                    if (v) next.add(site.id); else next.delete(site.id);
                                    setAdditionalSiteIds(next);
                                  }}
                                />
                                <span className="flex-1 text-sm">{site.name}</span>
                                {isPrimary && (
                                  <Badge variant="secondary" className="text-[10px]">Primary</Badge>
                                )}
                                {site.country && (
                                  <Badge variant="outline" className="text-[10px]">{site.country}</Badge>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <p className="text-xs text-muted-foreground">
                  Selected: {selectedSiteIds.length} site{selectedSiteIds.length === 1 ? '' : 's'} (including primary).
                </p>
              </div>
            )}

            <div className="space-y-2">
              {/* CR-7076: a role is a conscious, mandatory choice. The old
                  "No specific role" default silently minted roleless users
                  (no working permissions at all) — the dropdown now starts
                  empty and Next is blocked until one is picked. */}
              <Label>User Role *</Label>
              <Select value={profile.role?.toString() ?? ''}
                onValueChange={v => setProfile({ ...profile, role: parseInt(v) })}>
                <SelectTrigger><SelectValue placeholder="Select a user role" /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>3. Access configuration</CardTitle>
            <CardDescription>
              Modules, notifications, and the read-only override.
              Apply the same settings to every site, or override per site.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
              <div>
                <p className="font-medium text-sm">Apply same settings to every site</p>
                <p className="text-xs text-muted-foreground">
                  Turn off to fine-tune access per site below.
                </p>
              </div>
              <Switch checked={applyToAll} onCheckedChange={setApplyToAll} />
            </div>

            {applyToAll ? (
              <PermissionMatrix
                title="Default access (applies to every selected site)"
                flags={defaultFlags}
                onChange={(key, value) => setFlag(null, key, value)}
              />
            ) : (
              <div className="space-y-3">
                {selectedSiteIds.map(sid => {
                  const site = companies.find(c => c.id === sid);
                  const isPrimary = sid === primaryCompanyId;
                  return (
                    <PermissionMatrix
                      key={sid}
                      title={site?.name || `Site #${sid}`}
                      subtitle={isPrimary ? 'Primary company' : undefined}
                      flags={flagsForSite(sid)}
                      onChange={(key, value) => setFlag(sid, key, value)}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>4. Review &amp; send</CardTitle>
            <CardDescription>Sanity-check before the invitation email goes out.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <ReviewBlock label="Profile">
              <div>{profile.salutation && <span className="text-muted-foreground mr-1">{profile.salutation}</span>}<strong>{profile.first_name} {profile.last_name}</strong></div>
              <div className="text-muted-foreground">{profile.email}{profile.phone ? ` · ${profile.phone}` : ''}</div>
              {(profile.job_title || profile.department) && (
                <div className="text-xs text-muted-foreground mt-1">
                  {profile.job_title}{profile.job_title && profile.department ? ' · ' : ''}{profile.department}
                </div>
              )}
            </ReviewBlock>

            <ReviewBlock label="Companies">
              <div><strong>Primary:</strong> {primaryCompany?.name || '—'}</div>
              {additionalSiteIds.size > 0 && (
                <div className="mt-1">
                  <strong>Additional sites:</strong>
                  <ul className="list-disc list-inside text-muted-foreground">
                    {Array.from(additionalSiteIds).map(sid => (
                      <li key={sid}>{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                User Role: {roles.find(r => r.id === profile.role)?.name || '—'}
              </div>
            </ReviewBlock>

            <ReviewBlock label="Access">
              <div className="text-xs text-muted-foreground mb-1">
                {applyToAll ? 'Same settings applied to every site' : 'Per-site overrides'}:
              </div>
              {applyToAll ? (
                <PermissionSummary flags={defaultFlags} />
              ) : (
                <div className="space-y-2">
                  {selectedSiteIds.map(sid => (
                    <div key={sid} className="border-l-2 border-primary/40 pl-2">
                      <div className="font-medium">{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</div>
                      <PermissionSummary flags={flagsForSite(sid)} />
                    </div>
                  ))}
                </div>
              )}
            </ReviewBlock>

            {profile.message && (
              <ReviewBlock label="Welcome message">
                <p className="whitespace-pre-wrap text-muted-foreground italic">{profile.message}</p>
              </ReviewBlock>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step nav */}
      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" disabled={step === 1 || submitting}
          onClick={() => goTo((step - 1) as WizardStep)}>
          <ArrowLeft className="h-4 w-4 mr-2" />Back
        </Button>
        {step < 4 ? (
          <Button onClick={() => goTo((step + 1) as WizardStep)}>
            Next<ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || success}>
            {submitting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending…</>
            ) : (
              <><UserPlus className="h-4 w-4 mr-2" />Send invitation</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PermissionMatrixProps {
  title: string;
  subtitle?: string;
  flags: Required<UserSitePermissionFlags>;
  onChange: (key: keyof UserSitePermissionFlags, value: boolean) => void;
}

function PermissionMatrix({ title, subtitle, flags, onChange }: PermissionMatrixProps) {
  return (
    <div className="border rounded-lg p-3 space-y-4">
      <div>
        <p className="font-medium text-sm">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      {/* CR-7076 (ADMIN-MODEL redesign): the per-site "User role" toggles are
          gone from the UI. Administrator and Approver wrote flags no backend
          check ever read (admin authority lives on the user-level role);
          the Compliance module + notification toggles are hidden until the
          compliance module ships per-user gating. The underlying invitation
          fields stay in the payload types, DORMANT. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PermissionGroup label="Modules">
          <FlagRow icon={<FileCheck className="h-3.5 w-3.5" />} label="Purchasing"
            checked={flags.can_purchasing} onChange={v => onChange('can_purchasing', v)} />
          <FlagRow icon={<CheckCircle className="h-3.5 w-3.5" />} label="Quality"
            checked={flags.can_quality} onChange={v => onChange('can_quality', v)} />
        </PermissionGroup>

        <PermissionGroup label="Access Override">
          {/* Supersedes the user's role at this site: view everything,
              change nothing. */}
          <FlagRow icon={<Eye className="h-3.5 w-3.5" />} label="Read only"
            checked={flags.is_read_only} onChange={v => onChange('is_read_only', v)} />
        </PermissionGroup>

        <PermissionGroup label="Notifications">
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Procurement"
            checked={flags.notify_procurement} onChange={v => onChange('notify_procurement', v)} />
          <FlagRow icon={<Mail className="h-3.5 w-3.5" />} label="Quality"
            checked={flags.notify_quality} onChange={v => onChange('notify_quality', v)} />
        </PermissionGroup>
      </div>
    </div>
  );
}

function PermissionGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function FlagRow({ icon, label, checked, onChange }:
  { icon: React.ReactNode; label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-1.5">{icon}{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function PermissionSummary({ flags }: { flags: Required<UserSitePermissionFlags> }) {
  // CR-7076: no derived "Role:" line here. The old summary invented a
  // "Standard user" label from per-site toggles, contradicting the real
  // User Role shown one block up — the review now states the role exactly
  // once. Compliance stays hidden with its dormant toggles.
  const modules: string[] = [];
  if (flags.can_purchasing) modules.push('Purchasing');
  if (flags.can_quality) modules.push('Quality');
  const notify: string[] = [];
  if (flags.notify_procurement) notify.push('Procurement');
  if (flags.notify_quality) notify.push('Quality');
  return (
    <div className="text-xs text-muted-foreground space-y-0.5">
      <div><strong>Modules:</strong> {modules.length ? modules.join(', ') : '—'}</div>
      {flags.is_read_only && (
        <div><strong>Access Override:</strong> Read only — can view, cannot change</div>
      )}
      <div><strong>Notifications:</strong> {notify.length ? notify.join(', ') : 'None'}</div>
    </div>
  );
}

function ReviewBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-md p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{label}</p>
      {children}
    </div>
  );
}
